<template>
  <table class="full-width-table leads-tab-table">
    <tbody>
      <tr class="table-header">
        <th style="width: 16.67%">Date</th>
        <th style="width: 16.67%">Score</th>
        <th style="width: 16.67%">Source</th>
        <th style="width: 16.67%">Salesperson</th>
        <th style="width: 16.67%">Complete</th>
        <th style="width: 16.67%">Job Name</th>
      </tr>
      <tr v-if="!loading && leadEntries.length > 0" v-for="entry in leadEntries" :key="entry.LEADID" @click="openLead(entry)" class="lead-entry-row">
        <td>
          <span>{{ formatDate(entry.CREATEDATE) }}</span>
        </td>
        <td>
          <span :class="getScoreClass(entry.SCORE)">{{ formatScore(entry.SCORE) }}</span>
        </td>
        <td>
          <span>{{ entry.SOURCE }}</span>
        </td>
        <td>
          <span>{{ getDisplayName(entry.ASSIGNEDID) }}</span>
        </td>
        <td>
          <span :class="getCompleteClass(entry.COMPLETE)">{{ entry.COMPLETE ? 'Yes' : 'No' }}</span>
        </td>
        <td class="job-name-cell">
          <span class="job-name-text">{{ entry.JOBNAME || '' }}</span>
        </td>
      </tr>
      <tr v-if="!loading && leadEntries.length === 0" class="no-results">
        <td colspan="6">No leads found</td>
      </tr>
      <tr v-if="loading" class="loading-row">
        <td colspan="6"><img src="../assets/spinner.svg" /></td>
      </tr>
    </tbody>
  </table>
  <AddLead v-if="showAddLead" :initContactId="{ type, entityid }" @closePopup="closeAddLead" />
</template>

<script>
import { ref, computed, watch } from 'vue';
import useContactLeadsQuery from '@/composables/useContactLeadsQuery';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useUsersQuery from '@/composables/useUsersQuery';
import useSingleLeadQuery from '@/composables/useSingleLeadQuery';
import AddLead from '@/components/AddLead.vue';

export default {
  name: 'LeadsTab',
  components: {
    AddLead,
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['company', 'contact'].includes(value),
    },
    entityid: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const queryContactLeads = useContactLeadsQuery();
    const { decryptSymbols } = useDecryptSymbols();
    const { allActiveUsers, getUsers } = useUsersQuery();
    const { showSingleLead } = useSingleLeadQuery;

    getUsers();

    // Reactive refs
    const leadEntries = ref([]);
    const leadQuery = ref(null);

    // Computed properties
    const loading = computed(() => {
      return leadQuery.value?.loading || false;
    });

    const error = computed(() => {
      return leadQuery.value?.error || null;
    });

    // Execute query function
    const executeQuery = () => {
      if (!props.entityid || props.entityid === 'new') {
        leadQuery.value = null;
        leadEntries.value = [];
        return;
      }

      // Set up query parameters based on type
      const queryParams = {};
      if (props.type === 'company') {
        queryParams.companyid = props.entityid;
      } else if (props.type === 'contact') {
        queryParams.contactid = props.entityid;
      }

      leadQuery.value = queryContactLeads(queryParams);
    };

    // Watch for props changes
    watch(() => [props.type, props.entityid], executeQuery, { immediate: true });

    // Watch for query result changes and update leadEntries
    watch(
      () => leadQuery.value?.result?.contactLeads,
      (newResult) => {
        if (newResult) {
          const decryptedEntries = decryptSymbols(newResult);
          // Sort by date created, newest first
          leadEntries.value = decryptedEntries.sort((a, b) => {
            // Try to parse as integer timestamp first
            const timestampA = parseInt(a.CREATEDATE);
            const timestampB = parseInt(b.CREATEDATE);

            if (!isNaN(timestampA) && !isNaN(timestampB)) {
              return timestampB - timestampA; // Descending order (newest first)
            }

            // Fall back to date parsing
            const dateA = new Date(a.CREATEDATE);
            const dateB = new Date(b.CREATEDATE);
            return dateB - dateA; // Descending order (newest first)
          });
        } else {
          leadEntries.value = [];
        }
      },
      { immediate: true, deep: true }
    );

    watch(
      () => showSingleLead.value,
      (newVal) => {
        if (!newVal) setTimeout(executeQuery, 500);
      }
    );

    return {
      loading,
      error,
      leadEntries,
      executeQuery,
      allActiveUsers,
      showSingleLead,
    };
  },
  data() {
    return {
      showAddLead: false,
    };
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';

      try {
        // Try to parse as integer timestamp first
        const timestamp = parseInt(dateString);
        if (!isNaN(timestamp)) {
          const date = new Date(timestamp);
          return date.toLocaleDateString('en-US');
        }

        // Fall back to string parsing
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US');
      } catch (error) {
        console.error('Error parsing date:', error);
        return '';
      }
    },
    formatScore(score) {
      if (!score) return '';

      // Capitalize first letter and handle common score values
      const formattedScore = score.charAt(0).toUpperCase() + score.slice(1).toLowerCase();
      return formattedScore;
    },
    getScoreClass(score) {
      if (!score) return '';

      const lowerScore = score.toLowerCase();
      return `score-${lowerScore}`;
    },
    getCompleteClass(complete) {
      return complete ? 'complete-yes' : 'complete-no';
    },
    getDisplayName(assignedId) {
      if (!assignedId) return 'Unassigned';

      const user = this.allActiveUsers.find((user) => user.USERID === assignedId);
      return user?.DISPLAYNAME || 'Unknown';
    },
    getLeadDisplayName(lead) {
      // Show lead name if available, otherwise show job name, otherwise show company/contact name
      if (lead.NAME && lead.NAME.trim()) {
        return lead.NAME;
      }

      if (lead.JOBNAME && lead.JOBNAME.trim()) {
        return lead.JOBNAME;
      }

      // Fall back to company or contact name
      if (lead.company?.NAME) {
        return lead.company.NAME;
      }

      if (lead.contact?.FIRSTNAME || lead.contact?.LASTNAME) {
        return `${lead.contact.FIRSTNAME || ''} ${lead.contact.LASTNAME || ''}`.trim();
      }

      return 'Unnamed Lead';
    },
    openLead(lead) {
      // Set the showSingleLead value to open the popup
      this.showSingleLead = lead.LEADID;
    },
    addLead() {
      this.showAddLead = true;
    },
    closeAddLead() {
      this.showAddLead = false;
      this.executeQuery();
    },
  },
};
</script>

<style lang="scss" scoped>
.leads-tab-table {
  th:first-child,
  td:first-child {
    padding-left: 3.75rem;
  }

  td {
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  .loading-row {
    td {
      background: white;
    }
  }
}
</style>
